import { Modal, Stack, Select, TextInput, Textarea, Button, Group, Text, Collapse, NumberInput, Box } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconChevronDown } from '@tabler/icons-react';
import { useChildren, useCreateAssignment } from '../hooks/useParent';
import { useThemes } from '@/features/story/hooks/useStory';
import { useDecks } from '@/features/flashcards/hooks/useFlashcards';
import { useState } from 'react';
import type { StudentProfile } from '@/lib/api-client';

type AssignmentType = 'story' | 'story_theme' | 'flashcard_deck';

interface AssignmentSubmitData {
  assignee_id: string;
  assignable_type: AssignmentType;
  assignable_id: string;
  title: string;
  instructions?: string;
  due_date?: string;
  assignment_metadata?: Record<string, unknown>;
}

// Language options for story generation
const LANGUAGE_OPTIONS = [
  { value: 'en-US', label: 'English' },
  { value: 'ne-NP', label: 'Nepali (नेपाली)' },
];

interface AssignmentModalProps {
  opened: boolean;
  onClose: () => void;
  type: AssignmentType;

  // Optional: Pre-selected values
  studentId?: string;
  studentName?: string;

  // For story assignments
  storyId?: string;
  storyTitle?: string;
  storyTheme?: string;

  // For custom handling (flashcard modal compatibility)
  children?: StudentProfile[];
  onSubmit?: (data: { data: AssignmentSubmitData; itemName: string; studentName: string }) => void;
  loading?: boolean;
}

export function AssignmentModal({
  opened,
  onClose,
  type,
  studentId,
  studentName,
  storyId,
  storyTitle,
  storyTheme,
  children: childrenProp,
  onSubmit: onSubmitProp,
  loading: loadingProp,
}: AssignmentModalProps) {
  const { data: childrenData } = useChildren();
  const { data: themes, isLoading: themesLoading } = useThemes();
  const { data: decks, isLoading: decksLoading } = useDecks();
  const createAssignment = useCreateAssignment();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const children = childrenProp || childrenData;
  const loading = loadingProp ?? createAssignment.isPending;

  // Determine default title based on type
  const getDefaultTitle = () => {
    if (type === 'story' && storyTitle) return `Read: ${storyTitle}`;
    if (type === 'story_theme') return '';
    if (type === 'flashcard_deck') return '';
    return '';
  };

  const form = useForm({
    initialValues: {
      child_id: studentId || '',
      title: getDefaultTitle(),
      instructions: '',
      resource_id: storyId || '',
      due_date: null as Date | null,
      language_code: 'en-US', // Default language for stories
      // Flashcard-specific
      mastery_threshold: 80,
      required_reviews: 1,
    },
    validate: {
      child_id: (value) => (!value ? 'Please select a child' : null),
      title: (value) => (!value ? 'Title is required' : null),
      resource_id: (value) => (!value ? `Please select a ${type === 'flashcard_deck' ? 'deck' : 'theme'}` : null),
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    // Find the selected child to get their id (student profile id)
    const selectedChild = children?.find((child) => child.id === values.child_id);
    if (!selectedChild) return;

    const baseData: AssignmentSubmitData = {
      assignee_id: selectedChild.user_id, // Use user_id - assignments FK references users table
      assignable_type: type,
      assignable_id: type === 'story' ? storyId! : values.resource_id,
      title: values.title,
      instructions: values.instructions || undefined,
      due_date: values.due_date?.toISOString() || undefined,
    };

    // Get item name for notification
    let itemName = values.title;
    if (type === 'flashcard_deck') {
      const deck = decks?.find(d => d.id === values.resource_id);
      itemName = deck?.title || values.title;
    } else if (type === 'story_theme') {
      const theme = themes?.find(t => t.id === values.resource_id);
      itemName = theme?.label || values.title;
    } else if (type === 'story' && storyTitle) {
      itemName = storyTitle;
    }

    // Handle flashcard-specific metadata
    if (type === 'flashcard_deck') {
      const flashcardData = {
        ...baseData,
        assignment_metadata: {
          resource_url: `/flashcards/review/${values.resource_id}`,
          mastery_threshold: values.mastery_threshold / 100,
          required_reviews: values.required_reviews,
        },
      };

      if (onSubmitProp) {
        onSubmitProp({
          data: flashcardData,
          itemName,
          studentName: selectedChild.display_name,
        });
        form.reset();
      } else {
        createAssignment.mutate({
          data: flashcardData,
          itemName,
          studentName: selectedChild.display_name,
        }, {
          onSuccess: () => {
            form.reset();
            onClose();
          },
        });
      }
      return;
    }

    // Handle story metadata with language code
    if (type === 'story') {
      baseData.assignment_metadata = {
        theme: storyTheme,
        language_code: values.language_code
      };
    } else if (type === 'story_theme') {
      // For theme assignments, add language_code to metadata
      baseData.assignment_metadata = {
        language_code: values.language_code
      };
    }

    if (onSubmitProp) {
      onSubmitProp({
        data: baseData,
        itemName,
        studentName: selectedChild.display_name,
      });
      form.reset();
    } else {
      createAssignment.mutate({
        data: baseData,
        itemName,
        studentName: selectedChild.display_name,
      }, {
        onSuccess: () => {
          form.reset();
          onClose();
        },
      });
    }
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const getModalTitle = () => {
    if (type === 'story') return 'Assign Story';
    if (type === 'story_theme') return 'Assign Reading';
    if (type === 'flashcard_deck') return 'Assign Flashcards';
    return 'Create Assignment';
  };

  const getResourceLabel = () => {
    if (type === 'flashcard_deck') return 'Deck';
    if (type === 'story_theme') return 'Theme';
    return 'Resource';
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={getModalTitle()}
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          {/* Child and Resource Selection */}
          <Group grow align="flex-start">
            <Select
              label="Child"
              placeholder="Select child"
              required
              size="sm"
              data={
                children?.map((child) => ({
                  value: child.id,
                  label: child.display_name,
                })) || []
              }
              {...form.getInputProps('child_id')}
              disabled={!!studentId}
              description={studentId ? studentName : undefined}
            />

            {type === 'story' ? (
              <Stack
                gap={0}
                styles={{
                  root: {
                    flex: 1,
                  },
                }}
              >
                <Text size="sm" fw={500} c="dimmed">
                  Story
                </Text>
                <Text size="sm" lineClamp={1} title={storyTitle}>
                  {storyTitle}
                </Text>
              </Stack>
            ) : type === 'story_theme' ? (
              <Select
                label="Theme"
                placeholder="Select theme"
                required
                size="sm"
                data={
                  themes
                    ?.filter((theme) => theme.is_active)
                    .sort((a, b) => a.display_order - b.display_order)
                    .map((theme) => ({
                      value: theme.id,
                      label: theme.emoji ? `${theme.emoji} ${theme.label}` : theme.label,
                    })) || []
                }
                searchable
                disabled={themesLoading}
                {...form.getInputProps('resource_id')}
              />
            ) : (
              <Select
                label="Deck"
                placeholder="Select deck"
                required
                size="sm"
                disabled={decksLoading}
                data={
                  decks?.map((deck) => ({
                    value: deck.id,
                    label: `${deck.title} (${deck.card_count || 0})`,
                  })) || []
                }
                {...form.getInputProps('resource_id')}
                searchable
              />
            )}
          </Group>

          <TextInput
            label="Title"
            placeholder={
              type === 'flashcard_deck'
                ? 'e.g., Study Spanish Vocabulary'
                : 'e.g., Read a story about courage'
            }
            required
            size="sm"
            {...form.getInputProps('title')}
          />

          {/* Language selector for story-related assignments */}
          {(type === 'story' || type === 'story_theme') && (
            <Select
              label="Story Language"
              placeholder="Select language"
              size="sm"
              data={LANGUAGE_OPTIONS}
              {...form.getInputProps('language_code')}
              description="Language for the story content"
            />
          )}

          <Button
            variant="subtle"
            size="xs"
            onClick={() => setShowAdvanced(!showAdvanced)}
            rightSection={
              <Box
                component="span"
                styles={{
                  root: {
                    display: 'inline-flex',
                    transform: showAdvanced ? 'rotate(180deg)' : 'none',
                    transition: 'transform 200ms',
                  },
                }}
              >
                <IconChevronDown size={14} />
              </Box>
            }
            styles={{
              root: {
                alignSelf: 'flex-start',
              },
            }}
          >
            {showAdvanced ? 'Hide' : 'Show'} details
          </Button>

          <Collapse in={showAdvanced}>
            <Stack gap="sm">
              <Group align="flex-start">
                <DateInput
                  label="Due Date"
                  placeholder="Pick a date"
                  minDate={new Date()}
                  clearable
                  size="sm"
                  w={200}
                  {...form.getInputProps('due_date')}
                />
              </Group>

              <Textarea
                label="Instructions"
                placeholder="Additional details (optional)..."
                minRows={2}
                size="sm"
                {...form.getInputProps('instructions')}
              />

              {type === 'flashcard_deck' && (
                <Group grow>
                  <NumberInput
                    label="Mastery %"
                    description="Required mastery"
                    placeholder="80"
                    min={0}
                    max={100}
                    suffix="%"
                    size="sm"
                    {...form.getInputProps('mastery_threshold')}
                  />

                  <NumberInput
                    label="Sessions"
                    description="Min study sessions"
                    placeholder="1"
                    min={1}
                    size="sm"
                    {...form.getInputProps('required_reviews')}
                  />
                </Group>
              )}
            </Stack>
          </Collapse>

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={handleClose} size="sm">
              Cancel
            </Button>
            <Button type="submit" loading={loading} size="sm">
              Create
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
